import React, { useState, useEffect } from 'react'
import { AlertCircle, Sparkles, Loader2, RotateCcw, Send, Clock, Hash } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import { useCaptionConversation, useGalleryPostContext } from '@/features/social/hooks'
import type { CaptionAgentContext } from '@/features/social/types/captionAgent'

interface PostContentEditorProps {
  caption: string
  onCaptionChange: (value: string) => void
  onBlur?: () => void
  platform?: string
  error?: string | null
  isSubmitting: boolean
  postContext?: CaptionAgentContext
  profileId: string
  imageUrls: string[]
  hashtags: string[]
}

export function PostContentEditor({
  caption,
  onCaptionChange,
  onBlur,
  platform,
  error,
  isSubmitting,
  postContext,
  profileId,
  imageUrls,
  hashtags
}: PostContentEditorProps) {
  const { error: toastError, success: toastSuccess } = useToast()
  const [agentInput, setAgentInput] = useState('')
  
  const galleryContextQuery = useGalleryPostContext({ context: postContext })
  const {
    messages: agentMessages,
    status: agentStatus,
    error: agentError,
    hasContext: agentHasContext,
    startConversation,
    sendInstruction,
    resetConversation
  } = useCaptionConversation({ postContext })

  useEffect(() => {
    if (agentError) {
      toastError('AI Caption Error', agentError)
    }
  }, [agentError, toastError])

  const agentHasStarted = agentMessages.length > 0
  const agentLoading = agentStatus === 'starting' || agentStatus === 'updating'
  const latestAssistantMessage = agentMessages.filter(message => message.role === 'assistant').slice(-1)[0]

  const ensureAgentPreconditions = () => {
    if (!agentHasContext || !postContext) {
      toastError('AI Caption Unavailable', 'This post is not linked to a job or gallery.')
      return false
    }

    if (!profileId) {
      toastError('Profile Required', 'Select a profile before using the AI caption assistant.')
      return false
    }

    if (imageUrls.length === 0) {
      toastError('Images Required', 'Add at least one approved job image before generating a caption.')
      return false
    }

    return true
  }

  const applyAgentResult = (newCaption: string) => {
    onCaptionChange(newCaption)
    toastSuccess('Caption updated', 'AI caption applied to your draft.')
  }

  const handleAgentGenerate = async () => {
    if (!ensureAgentPreconditions()) return
    try {
      const result = await startConversation({
        platform: platform,
        profileId: profileId,
        imageUrls: imageUrls,
        hashtags: hashtags
      })
      applyAgentResult(result.caption)
    } catch (error) {
      const message = error instanceof Error ? error.message : undefined
      toastError('AI Caption Error', message || 'Unable to generate caption')
    }
  }

  const handleAgentRefine = async () => {
    if (!ensureAgentPreconditions()) return
    if (!agentInput.trim()) {
      toastError('Instruction Needed', 'Tell the AI how to refine the caption.')
      return
    }

    try {
      const result = await sendInstruction(agentInput, {
        platform: platform,
        profileId: profileId,
        imageUrls: imageUrls,
        hashtags: hashtags
      })
      applyAgentResult(result.caption)
      setAgentInput('')
    } catch (error) {
      const message = error instanceof Error ? error.message : undefined
      toastError('AI Caption Error', message || 'Unable to refine caption')
    }
  }

  const handleAgentReset = () => {
    resetConversation()
    setAgentInput('')
  }

  const renderCaptionAgentPanel = () => {
    if (!postContext) return null
    const gallerySummary = galleryContextQuery.data
    
    return (
      <div className="rounded-xl border border-border/70 bg-muted/20 p-4 space-y-4 mt-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2 font-semibold text-sm">
              <Sparkles className="h-4 w-4 text-primary" />
              AI Caption Assistant
            </div>
            <p className="text-xs text-muted-foreground max-w-xl">
              Generate captions from gallery jobs, then refine the tone with follow-up instructions.
            </p>
            {gallerySummary && (
              <div className="text-xs text-muted-foreground flex flex-wrap gap-2">
                {gallerySummary.jobNumber && (
                  <Badge variant="outline" className="text-2xs">
                    Job #{gallerySummary.jobNumber}
                  </Badge>
                )}
                {gallerySummary.location && (
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {gallerySummary.location}
                  </span>
                )}
                {gallerySummary.tags.length > 0 && (
                  <span className="inline-flex items-center gap-1">
                    <Hash className="h-3 w-3" />
                    {gallerySummary.tags.slice(0, 3).join(', ')}
                    {gallerySummary.tags.length > 3 && '…'}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {agentHasStarted && (
              <Button variant="outline" size="sm" onClick={handleAgentReset} disabled={agentLoading}>
                <RotateCcw className="mr-2 h-3 w-3" />
                Reset
              </Button>
            )}
          </div>
        </div>

        {!agentHasStarted ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => { void handleAgentGenerate() }}
              disabled={!agentHasContext || agentLoading}
              className="w-full sm:w-auto"
            >
            {agentLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Preparing caption…
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Generate from Job
              </>
            )}
          </Button>
        ) : (
          <>
            <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
              {agentMessages.map(message => (
                <div
                  key={message.id}
                  className={cn(
                    'rounded-lg px-3 py-2 text-sm',
                    message.role === 'assistant'
                      ? 'bg-primary/5 border border-primary/20 text-foreground'
                      : 'bg-muted text-muted-foreground'
                  )}
                >
                  <div className="text-xs font-semibold uppercase tracking-wide mb-1">
                    {message.role === 'assistant' ? 'Assistant' : 'You'}
                  </div>
                  <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <Textarea
                value={agentInput}
                onChange={event => setAgentInput(event.target.value)}
                placeholder="Ask for tweaks (e.g. “shorter and mention the neighborhood”)."
                disabled={agentLoading}
                rows={3}
              />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => { void handleAgentRefine() }}
                  disabled={agentLoading}
                >
                  {agentLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Refining…
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Send Instruction
                    </>
                  )}
                </Button>
                {latestAssistantMessage && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => applyAgentResult(latestAssistantMessage.content)}
                  >
                    Use Latest Reply
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    )
  }

  const maxChars = platform === 'INSTAGRAM' ? 2200 :
    platform === 'TWITTER' ? 280 :
    platform === 'LINKEDIN' ? 3000 :
    platform === 'FACEBOOK' ? 63206 : 2200

  return (
    <div className="space-y-2">
      <Label htmlFor="caption" className="text-sm font-medium text-muted-foreground">
        Write your caption
      </Label>
      <Textarea
        id="caption"
        name="caption"
        value={caption}
        onChange={(e) => onCaptionChange(e.target.value)}
        onBlur={onBlur}
        rows={6}
        placeholder="What's on your mind?"
        disabled={isSubmitting}
        className={cn(
          "resize-none text-base p-4 min-h-40",
          error && "border-destructive focus:ring-destructive"
        )}
      />
      <div className="flex items-center justify-between pt-1">
        <p className={cn(
          "text-xs font-medium",
          caption.length > maxChars
            ? "text-destructive"
            : "text-muted-foreground"
        )}>
          {caption.length} / {maxChars.toLocaleString()} characters
        </p>
        {platform && (
          <Badge variant="secondary" className="text-xs font-normal">
            {platform}
          </Badge>
        )}
      </div>
      {error && (
        <div className="flex items-center gap-1 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}
      {renderCaptionAgentPanel()}
    </div>
  )
}

